/**
 * Sentry configuration for the customer quote request app.
 *
 * Three things here are deliberate and were all wrong before — see
 * docs/technical/error-monitoring-plan.md §1:
 *
 *   1. `environment` comes from REACT_APP_SENTRY_ENV, NOT NODE_ENV. CRA sets
 *      NODE_ENV=production for every `npm run build`, including the dev and test
 *      site deployments, so deriving the environment from it filed every
 *      environment's errors under "production" and made the filter useless.
 *   2. `release` carries the git sha. scripts/deploy.js builds the whole string
 *      and injects it as REACT_APP_SENTRY_RELEASE, handing the identical value
 *      to `sentry-cli` for the source map upload; the app versions have not
 *      moved off 1.0.0, so without the sha two builds are indistinguishable.
 *   3. Session replays are recorded ONLY when something breaks. The free tier
 *      allows 50 replays a month. At the previous 10% session sampling that was
 *      spent after ~500 sessions, mostly on sessions where nothing happened, and
 *      the error replays — the entire point — were dropped for the rest of the
 *      month.
 *
 * A build with no DSN configured initialises nothing at all (see src/index.js).
 * That is the normal state for local development.
 */

/** Public by design — a DSN is readable in any bundle that ships it. */
const dsn = process.env.REACT_APP_SENTRY_DSN || '';

/** `dev` | `test` | `production`, matching the vocabulary scripts/deploy.js uses. */
const environment = process.env.REACT_APP_SENTRY_ENV || 'development';

/**
 * The complete release string, built and injected by scripts/deploy.js, which
 * hands the identical value to `sentry-cli` for the source map upload. Reported
 * VERBATIM — do not reassemble it here. The fallback applies only to builds
 * nothing uploads maps for, so it can never mismatch anything.
 */
const release = process.env.REACT_APP_SENTRY_RELEASE || 'panelgear-customer-rfq@0.0.0-local';

/**
 * Route events through our own domain instead of straight to Sentry.
 *
 * Content blockers and Firefox's Enhanced Tracking Protection drop requests to
 * `*.ingest.sentry.io` before they leave the browser, so without this the errors
 * that go unreported are exactly the ones hitting the most privacy-conscious
 * users — a silent, self-selecting blind spot.
 *
 * Unset means no tunnel and the SDK talks to Sentry directly, which is the
 * correct behaviour until `sentry-tunnel` is deployed on a domain of ours.
 */
const tunnel = process.env.REACT_APP_SENTRY_TUNNEL || '';

const sentryConfig = {
  dsn,
  environment,
  ...(tunnel ? { tunnel } : {}),
  enabled: Boolean(dsn),

  release,

  debug: false,

  /**
   * IP addresses and request bodies do not ride along on error events. Replays
   * are a deliberate, documented exposure (plan §3.3); events need not double it.
   */
  sendDefaultPii: false,

  tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

  /** See note 3 above. 50 replays a month, spent only on broken sessions. */
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  /**
   * Empty on purpose. Appwrite rejects the `baggage` and `sentry-trace` headers,
   * so tracing a request to it fails CORS and the request never leaves the
   * browser — and Appwrite is the only backend this app talks to. The
   * azurewebsites pattern this replaced matched nothing in any app.
   */
  tracePropagationTargets: [],

  /**
   * Noise filtered at the client, because it is cheaper here: an event dropped
   * by Sentry's own inbound filters has already been counted against the 5,000
   * errors the free tier allows.
   */
  ignoreErrors: [
    // Benign, fires on any resize-driven layout and is not actionable.
    /ResizeObserver loop/,
    // The user navigated away mid-request. Not a fault.
    'AbortError',
    'The operation was aborted',
    'TypeError: Failed to fetch',
    'TypeError: NetworkError when attempting to fetch resource.',
    'TypeError: cancelled',
    // A rejection with no Error in it carries no stack and cannot be diagnosed.
    'Non-Error promise rejection captured',
    // Browser extensions and injected scripts, none of which are our code.
    'top.GLOBALS',
    /^ResizeObserver/,
    'Java exception was raised during method invocation',
  ],

  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
  ],

  /**
   * Last line of defence after ignoreErrors and denyUrls, aimed at one specific
   * kind of noise: scripts injected into the page that carry no real filename.
   *
   * Deliberately NOT keyed on `in_app`. Once source maps are uploaded (plan
   * §3.9) library frames stop being in_app, and an error thrown entirely inside
   * the Appwrite SDK or React would be dropped silently — which is exactly the
   * error worth seeing.
   */
  beforeSend(event) {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (!frames?.length) return event;
    return frames.some((frame) => /^https?:\/\//.test(frame.filename || '')) ? event : null;
  },
};

export default sentryConfig;
